import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { catchError, filter, finalize, Observable, of, take } from 'rxjs';
import { BANNER_TYPE } from 'src/app/components/banner/banner.constants';
import {
  CreateDeliveryRequest,
  CreateDeliveryResponse,
  ServiceLevelCodsResponse,
  ValidateDeliveryPackageResponse,
} from 'src/app/core/models/operations/operations-delivery.model';
import { DeletePackageResponse } from 'src/app/core/models/operations/operations-pickup-stop.model';
import { CommonUtils } from 'src/app/core/utils/common/common.utils';
import { environment } from 'src/environments/environment';
import { ErrorhandleService } from '../../error-handling/error-handle/errorhandle.service';
import { tap } from 'rxjs/operators';
import { DataService } from 'src/app/core/services/data/data.service';

@Injectable({
  providedIn: 'root',
})
export class DeliveryService {
  baseUrl = environment.apiUrl.concat('operations-management');

  constructor(
    private readonly http: HttpClient,
    private readonly commonUtils: CommonUtils,
    private readonly errorHandler: ErrorhandleService,
    private readonly dataService: DataService,
  ) {}

  createDelivery(
    body: CreateDeliveryRequest,
  ): Observable<CreateDeliveryResponse> {
    return this.http
      .post<CreateDeliveryResponse>(`${this.baseUrl}/delivery/`, body)
      .pipe(
        take(1),
        catchError((error) =>
          this.errorHandler.handleError<CreateDeliveryResponse>(
            error,
            error.status,
            'Delivery Service: Create Delivery',
            BANNER_TYPE.API_400_ERROR,
            error,
            'delivery',
          ),
        ),
        tap((res) => {
          try {
            if (res?.code === 200) {
              const stopId = res?.response?.stopId ?? null;
              const info = body?.deliveryDetail?.infoNotice ?? '';
              if (stopId != null && typeof info === 'string') {
                this.dataService.setInfoNotice(stopId, info);
              }
            }
          } catch {}
        }),
        filter((value) => !!value),
      );
  }

  deleteDelivery(
    body: CreateDeliveryRequest,
    location: string,
    loop: string,
  ): Observable<DeletePackageResponse> {
    return this.http
      .post<DeletePackageResponse>(
        `${this.baseUrl}/deletedeliverypackage`,
        body,
        {
          headers: {
            location: location,
            loop: loop,
          },
        },
      )
      .pipe(
        take(1),
        catchError((response: HttpErrorResponse) => {
          return this.errorHandler.handleError<DeletePackageResponse>(
            response,
            response.status,
            'Delivery Service: Delete Delivery Stop',
            BANNER_TYPE.API_400_ERROR,
            response as unknown as DeletePackageResponse,
          );
        }),
        filter((value) => !!value),
      );
  }

  getServiceLevelCods(): Observable<ServiceLevelCodsResponse> {
    this.commonUtils.updateLoadingModal(true);
    return this.http
      .get<ServiceLevelCodsResponse>(`${this.baseUrl}/service-cod-required`)
      .pipe(
        take(1),
        catchError((error) =>
          this.errorHandler.handleError<ServiceLevelCodsResponse>(
            error,
            error.status,
            'Delivery Service: Service COD Required',
            BANNER_TYPE.API_400_ERROR,
            error,
          ),
        ),
        filter((value) => !!value),
        finalize(() => {
          this.commonUtils.updateLoadingModal(false);
        }),
      );
  }

  validateDeliveryPackage(
    trackingNumbers: string[],
  ): Observable<ValidateDeliveryPackageResponse> {
    this.commonUtils.updateLoadingModal(true);

    return this.http
      .get<ValidateDeliveryPackageResponse>(
        `${this.baseUrl}/delivery/validate`,
        { params: { trackNumList: trackingNumbers } },
      )
      .pipe(
        take(1),
        catchError((error: HttpErrorResponse) => {
          const message = error?.error?.message || '';

          if (error.status === 400) {
            if (message.startsWith('Invalid Tracking Number')) {
              return of(null as unknown as ValidateDeliveryPackageResponse);
            }

            if (message.startsWith('DO NOT DELIVER! Package Intercept')) {
              return of({ message } as ValidateDeliveryPackageResponse);
            }
          }

          return this.errorHandler.handleError<ValidateDeliveryPackageResponse>(
            error,
            error.status,
            'Delivery Service: Validate Delivery Package Service',
            BANNER_TYPE.API_400_ERROR,
          );
        }),
        finalize(() => {
          this.commonUtils.updateLoadingModal(false);
        }),
      );
  }
}
